import { apiClient } from "@api/client";
import { request } from "@api/request";
import type { components } from "@api/schema";
import { ClinicAuthError } from "@auth/clinicAuthClient";

/** `bridged` means the leg was joined, not that a person picked up. */
export type PhoneCallStatus = components["schemas"]["PhoneCallStatus"];

export type PhoneCallDisposition =
  components["schemas"]["PhoneCallDisposition"];

/** Mirrors brian's `PhoneCallOrigin` (models/phone_call.py). */
export type PhoneCallOrigin = components["schemas"]["PhoneCallOrigin"];

/** Mirrors brian's `PhoneCallOutcome` (models/phone_call.py). */
export type PhoneCallOutcome = components["schemas"]["PhoneCallOutcome"];

type PhoneCallCaptureStatus = components["schemas"]["PhoneCallCaptureStatus"];

type PhoneCallMessageState = components["schemas"]["PhoneCallMessageState"];

/**
 * `played` is the go-ahead to speak; `failed`/`unknown` mean brian can't
 * confirm the notice played. Mirrors brian's `PhoneCallDisclosureState`
 * (models/phone_call.py).
 */
export type PhoneCallDisclosureState =
  components["schemas"]["PhoneCallDisclosureState"];

const DISCLOSURE_STATES: ReadonlySet<string> =
  new Set<PhoneCallDisclosureState>([
    "none",
    "playing",
    "played",
    "failed",
    "unknown",
  ]);

export const toDisclosureState = (
  value: string | undefined,
): PhoneCallDisclosureState =>
  value !== undefined && DISCLOSURE_STATES.has(value)
    ? (value as PhoneCallDisclosureState)
    : "none";

/**
 * Orthogonal to `status`, which stays monotonic; `resuming` covers Pick up
 * until the staff leg answers. Mirrors brian's `PhoneCallHoldState`
 * (models/phone_call.py).
 */
export type PhoneCallHoldState = components["schemas"]["PhoneCallHoldState"];

const HOLD_STATES: ReadonlySet<string> = new Set<PhoneCallHoldState>([
  "none",
  "held",
  "resuming",
]);

export const toHoldState = (value: string | undefined): PhoneCallHoldState =>
  value !== undefined && HOLD_STATES.has(value)
    ? (value as PhoneCallHoldState)
    : "none";

export const disclosureNoteFor = (
  bridged: boolean,
  disclosureState: PhoneCallDisclosureState,
): { playing: boolean; note: string | null } => {
  if (!bridged) return { playing: false, note: null };
  if (disclosureState === "playing") return { playing: true, note: null };
  if (disclosureState === "failed" || disclosureState === "unknown") {
    return { playing: false, note: "Recording notice may not have played." };
  }
  return { playing: false, note: null };
};

/** Mirrors brian's `PhoneCallOut` (modules/phone/schemas.py). */
export interface PhoneCall {
  id: string;
  origin: PhoneCallOrigin;
  /** Outbound: the caller ID the recipient saw, never who was called. */
  fromNumber: string | null;
  toNumber: string;
  staffLineE164: string | null;
  /** As it read at admission; survives renames. */
  staffLineLabel: string | null;
  recipientE164: string | null;
  status: PhoneCallStatus;
  disposition: PhoneCallDisposition | null;
  captureStatus: PhoneCallCaptureStatus;
  startedAt: string | null;
  endedAt: string | null;
  createdAt: string;
  recordingArtifactUuid: string | null;
  messageTaskUuid: string | null;
  messageState: PhoneCallMessageState;
  /** Render on this, not `messageState`: older calls keep `none` while carrying a message. */
  message: string | null;
  summary: string | null;
  outcome: PhoneCallOutcome | null;
  /** Resolved server-side; never re-derive it from `message`. */
  callerName: string | null;
  clientUuid: string | null;
  clientName: string | null;
  clientPatients: string[] | null;
  /** Monotonic per call, bumped on every visible change. */
  revision: number | null;
  updatedAt: string | null;
  disclosureState: PhoneCallDisclosureState;
  holdState: PhoneCallHoldState;
  heldAt: string | null;
}

type PhoneCallOut = components["schemas"]["PhoneCallOut"];

export const toPhoneCall = (c: PhoneCallOut): PhoneCall => ({
  id: c.uuid,
  origin: c.origin,
  fromNumber: c.from_number,
  toNumber: c.to_number,
  staffLineE164: c.staff_line_e164,
  staffLineLabel: c.staff_line_label,
  recipientE164: c.recipient_e164,
  status: c.status,
  disposition: c.disposition,
  captureStatus: c.capture_status,
  startedAt: c.started_at,
  endedAt: c.ended_at,
  createdAt: c.created_at,
  recordingArtifactUuid: c.recording_artifact_uuid,
  messageTaskUuid: c.message_task_uuid,
  messageState: c.message_state,
  message: c.message,
  summary: c.summary,
  outcome: c.outcome,
  callerName: c.caller_name,
  clientUuid: c.client_uuid,
  clientName: c.client_name,
  clientPatients: c.client_patients,
  revision: c.revision,
  updatedAt: c.updated_at,
  disclosureState: c.disclosure_state,
  holdState: c.hold_state,
  heldAt: c.held_at,
});

export const isTerminalPhoneCallStatus = (
  status: PhoneCallStatus,
): status is "ended" => status === "ended";

/** Ordering for every path that races a live call's frames; brian never un-ends a call. */
export const isFresherPhoneCall = (
  held: Pick<PhoneCall, "status" | "revision" | "updatedAt">,
  incoming: Pick<PhoneCall, "status" | "revision" | "updatedAt">,
  { allowEqual = false }: { allowEqual?: boolean } = {},
): boolean => {
  if (
    isTerminalPhoneCallStatus(held.status) &&
    !isTerminalPhoneCallStatus(incoming.status)
  ) {
    return false;
  }
  if (held.revision !== null && incoming.revision !== null) {
    return allowEqual
      ? incoming.revision >= held.revision
      : incoming.revision > held.revision;
  }
  if (held.updatedAt !== null && incoming.updatedAt !== null) {
    const a = new Date(incoming.updatedAt).getTime();
    const b = new Date(held.updatedAt).getTime();
    return allowEqual ? a >= b : a > b;
  }
  return true;
};

/** Int in REST, string in a notification frame. */
export const toRevision = (
  raw: number | string | undefined | null,
): number | null => {
  if (raw === undefined || raw === null) return null;
  const value = typeof raw === "number" ? raw : Number(raw);
  return Number.isFinite(value) ? value : null;
};

const PHONE_CALLS_PAGE_SIZE = 50;

/** `nextCursor` is opaque: echo it back, never build or parse one. */
export interface PhoneCallPage {
  calls: PhoneCall[];
  nextCursor: string | null;
}

export const fetchPhoneCalls = (cursor?: string): Promise<PhoneCallPage> =>
  request(() =>
    apiClient.GET("/phone-calls", {
      params: { query: { cursor, limit: PHONE_CALLS_PAGE_SIZE } },
    }),
  ).then((page) => ({
    calls: page.items.map(toPhoneCall),
    nextCursor: page.next_cursor ?? null,
  }));

/** A foreign or unknown call is a `ClinicAuthError` with `status: 404`. */
export const fetchPhoneCall = (
  callUuid: string,
  signal?: AbortSignal,
): Promise<PhoneCall> =>
  request(() =>
    apiClient.GET("/phone-calls/{call_uuid}", {
      params: { path: { call_uuid: callUuid } },
      signal,
    }),
  ).then(toPhoneCall);

export const isOutbound = (call: Pick<PhoneCall, "origin">): boolean =>
  call.origin === "staff_outbound";

/**
 * brian's `_CANCELLABLE` (routes/phone_calls.py) narrowed by product: once the
 * recipient is ringing, hanging up on them is staff's call, not a button.
 */
export const isCancellable = (status: PhoneCallStatus): boolean =>
  status === "out_dialing_staff" || status === "out_awaiting_confirmation";

/** Idempotent on an ended call; 409 once past the cancellable phases. */
export const cancelPhoneCall = (callUuid: string): Promise<PhoneCall> =>
  request(() =>
    apiClient.POST("/phone-calls/{call_uuid}/cancel", {
      params: { path: { call_uuid: callUuid } },
    }),
  ).then(toPhoneCall);

/** Mirrors brian's `hold_refusal` (modules/phone/transitions.py). */
const HOLDABLE_STATUSES: ReadonlySet<PhoneCallStatus> =
  new Set<PhoneCallStatus>(["disclosing", "handling", "dialing"]);

export const isHoldable = (
  call: Pick<PhoneCall, "origin" | "status" | "holdState" | "disposition">,
): boolean =>
  call.origin === "inbound" &&
  call.holdState === "none" &&
  call.disposition === null &&
  HOLDABLE_STATUSES.has(call.status);

/**
 * A hold action's 409 `detail`. Mirrors brian's `hold_refusal`/
 * `held_action_refusal` (modules/phone/transitions.py) and the resume route's
 * `browser_*` codes (routes/phone_calls.py).
 */
export type HoldConflict =
  | "already_held"
  | "not_holdable"
  | "browser_unavailable"
  | "browser_busy"
  | "not_held";

const HOLD_CONFLICTS: ReadonlySet<string> = new Set<HoldConflict>([
  "already_held",
  "not_holdable",
  "browser_unavailable",
  "browser_busy",
  "not_held",
]);

export const holdConflictOf = (error: unknown): HoldConflict | null =>
  error instanceof ClinicAuthError &&
  error.status === 409 &&
  error.detail !== undefined &&
  HOLD_CONFLICTS.has(error.detail)
    ? (error.detail as HoldConflict)
    : null;

export const holdPhoneCall = (callUuid: string): Promise<PhoneCall> =>
  request(() =>
    apiClient.POST("/phone-calls/{call_uuid}/hold", {
      params: { path: { call_uuid: callUuid } },
    }),
  ).then(toPhoneCall);

/** brian rings before responding, so call `expectPickup` first. */
export const resumePhoneCall = (
  callUuid: string,
  installationId: string,
): Promise<PhoneCall> =>
  request(() =>
    apiClient.POST("/phone-calls/{call_uuid}/resume", {
      params: { path: { call_uuid: callUuid } },
      body: { installation_id: installationId },
    }),
  ).then(toPhoneCall);

export const voicemailPhoneCall = (callUuid: string): Promise<PhoneCall> =>
  request(() =>
    apiClient.POST("/phone-calls/{call_uuid}/voicemail", {
      params: { path: { call_uuid: callUuid } },
    }),
  ).then(toPhoneCall);
